/**
 * Coaching Chatbot Component
 * AI coach conversation interface with learning history
 */

import React, { useState, useEffect, useRef } from 'react';
import { Send, History, Loader, ChevronDown, ChevronRight, FileText, Headphones } from 'lucide-react';
import './CoachingChatbot.css';

// Import API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
  || (process.env.NODE_ENV === 'development' ? 'http://localhost:3002' : 'https://api.nutriluma.app');

interface Message {
  id: number;
  user_message: string;
  coach_response: string;
  recommended_content: Array<{
    title: string;
    sourceFile?: string;
    folder?: string;
    type: string;
    relevance: number;
    link?: string;
    description?: string;
    sources?: string[];
    contentType?: string;
  }>;
  created_at: string;
}

interface ChatProps {
  userId: string;
  mealContext?: object;
  onClose?: () => void;
}

export const CoachingChatbot: React.FC<ChatProps> = ({ userId, mealContext, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState('');
  const [expandedSources, setExpandedSources] = useState<Record<number, boolean>>({});
  const [expandedExcerpts, setExpandedExcerpts] = useState<Record<string, boolean>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadConversationHistory();
  }, [userId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversationHistory = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/coach/conversations?userId=${userId}&limit=10`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        setMessages(data.conversations.reverse());
      }
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim()) return;

    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/coach/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          message: inputValue,
          userId: userId,
          mealContext: mealContext
        })
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();

      const newMessage: Message = {
        id: Date.now(),
        user_message: inputValue,
        coach_response: data.message,
        recommended_content: data.recommendations || [],
        created_at: new Date().toISOString()
      };

      setMessages(prev => [...prev, newMessage]);
      setInputValue('');

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteMessage = async (conversationId: number) => {
    try {
      await fetch(`${API_BASE_URL}/api/coach/conversation/${conversationId}?userId=${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        credentials: 'include',
      });
      setMessages(prev => prev.filter(m => m.id !== conversationId));
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleSourcesExpanded = (messageId: number) => {
    setExpandedSources(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const toggleExcerptExpanded = (excerptKey: string) => {
    setExpandedExcerpts(prev => ({
      ...prev,
      [excerptKey]: !prev[excerptKey]
    }));
  };

  const truncateToWords = (text: string, wordLimit: number) => {
    const words = text.split(/\s+/);
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  if (showHistory) {
    return (
      <div className="chatbot-history">
        <div className="history-header">
          <h3>Learning History</h3>
          <button onClick={() => setShowHistory(false)} className="history-close-btn">
            ×
          </button>
        </div>

        <div className="history-list">
          {messages.length === 0 ? (
            <p className="history-empty">No conversations yet</p>
          ) : (
            messages.map((msg) => (
              <div key={msg.id} className="history-item">
                <p className="history-item-message">
                  {msg.user_message.substring(0, 50)}...
                </p>
                <p className="history-item-date">
                  {new Date(msg.created_at).toLocaleDateString()}
                </p>
                {msg.recommended_content.length > 0 && (
                  <div className="history-item-resources">
                    <span className="label">Resources: </span>
                    <span className="count">{msg.recommended_content.length} items</span>
                  </div>
                )}
                <button onClick={() => deleteMessage(msg.id)} className="history-delete-btn">
                  Delete
                </button>
              </div>
            ))
          )}
        </div>

        <button onClick={() => setShowHistory(false)} className="history-back-btn">
          Back to Chat
        </button>
      </div>
    );
  }

  return (
    <div className="coaching-chatbot-container">
      <div className="chatbot-header">
        <h2>Nutrition Coach</h2>
        <div className="chatbot-header-actions">
          <button onClick={() => setShowHistory(true)} title="View conversation history">
            <History size={20} />
          </button>
          {onClose && (
            <button onClick={onClose}>×</button>
          )}
        </div>
      </div>

      <div className="chatbot-messages">
        {messages.map((msg) => (
          <div key={msg.id} className="message-group">
            <div className="user-message-wrapper">
              <div className="user-message">{msg.user_message}</div>
            </div>

            <div className="coach-message-wrapper">
              <div className="coach-message">{msg.coach_response}</div>
            </div>

            {/* View Course Sources - Expandable section showing actual course excerpts */}
            {msg.recommended_content && msg.recommended_content.length > 0 && (
              <div className="course-sources-container">
                <button
                  className="course-sources-toggle"
                  onClick={() => toggleSourcesExpanded(msg.id)}
                >
                  {expandedSources[msg.id] ? (
                    <ChevronDown size={18} />
                  ) : (
                    <ChevronRight size={18} />
                  )}
                  <span className="course-sources-title">
                    View Course Sources ({msg.recommended_content.length})
                  </span>
                </button>

                {expandedSources[msg.id] && (
                  <div className="course-sources-list">
                    {msg.recommended_content.map((content, idx) => {
                      const excerptKey = `${msg.id}-${idx}`;
                      const isExpanded = expandedExcerpts[excerptKey];
                      const wordLimit = 150;
                      const displayText = isExpanded
                        ? content.description
                        : truncateToWords(content.description || '', wordLimit);
                      const needsTruncation = (content.description || '').split(/\s+/).length > wordLimit;

                      return (
                        <div key={idx} className="course-source-card">
                          <div className="course-source-header">
                            {content.contentType === 'course_pdf' ? (
                              <FileText size={16} className="source-icon" />
                            ) : (
                              <Headphones size={16} className="source-icon" />
                            )}
                            <div className="course-source-meta">
                              <div className="source-file-name">
                                {content.sourceFile || content.title}
                              </div>
                              {content.folder && (
                                <div className="source-folder">
                                  {content.folder}
                                </div>
                              )}
                            </div>
                            <div className="source-relevance">
                              {content.relevance}%
                            </div>
                          </div>

                          <div className="course-source-excerpt">
                            {displayText}
                          </div>

                          {needsTruncation && (
                            <button
                              className="excerpt-expand-btn"
                              onClick={() => toggleExcerptExpanded(excerptKey)}
                            >
                              {isExpanded ? 'Show less' : 'Read more'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {error && (
          <div className="chatbot-error">{error}</div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chatbot-input-area">
        <div className="chatbot-input-group">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me about your nutrition, meals, or any health goals..."
            className="chatbot-textarea"
            rows={2}
            disabled={isLoading}
          />
          <button
            onClick={sendMessage}
            disabled={isLoading || !inputValue.trim()}
            className="chatbot-send-btn"
          >
            {isLoading ? (
              <Loader size={20} className="animate-spin" />
            ) : (
              <Send size={20} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoachingChatbot;
